import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Tabs, Tab, TextField, Button, Paper,
  IconButton, Divider, Alert, Grid, Avatar, CircularProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { getSiteContent, updateSiteContent, uploadTeamImage } from '../../services/storage';

interface Stat  { value: string; label: string; }
interface Value { title: string; desc: string; image: string; }

interface TeamMember {
  name: string;
  qualification: string;
  role: string;
  image: string;
  desc: string;
  ausbildung: string[];
  expertise: string[];
  berufserfahrung: string[];
}

interface AboutContent {
  hero_title: string;
  hero_subtitle: string;
  hero_image: string;
  intro_label: string;
  intro_title: string;
  intro_text: string;
  intro_image: string;
  stats: Stat[];
  values: Value[];
  team: TeamMember[];
  cta_title: string;
  cta_text: string;
}

const EMPTY_MEMBER: TeamMember = {
  name: '', qualification: '', role: '', image: '', desc: '',
  ausbildung: [''], expertise: [''], berufserfahrung: [''],
};

const EMPTY_CONTENT: AboutContent = {
  hero_title: '', hero_subtitle: '', hero_image: '',
  intro_label: '', intro_title: '', intro_text: '', intro_image: '',
  stats: [{ value: '', label: '' }, { value: '', label: '' }, { value: '', label: '' }, { value: '', label: '' }],
  values: [{ title: '', desc: '', image: '' }, { title: '', desc: '', image: '' }, { title: '', desc: '', image: '' }, { title: '', desc: '', image: '' }],
  team: [],
  cta_title: '', cta_text: '',
};

function TabPanel({ children, value, index }: { children?: React.ReactNode; value: number; index: number }) {
  return <Box role="tabpanel" hidden={value !== index} sx={{ pt: 3 }}>{value === index && children}</Box>;
}

// Converts a TeamMember from DB (may have string[] stored as JSON strings) into proper arrays
function normalizeTeam(raw: unknown[]): TeamMember[] {
  return raw.map((m: unknown) => {
    const mb = m as Record<string, unknown>;
    const toArr = (v: unknown): string[] => {
      if (Array.isArray(v)) return v as string[];
      if (typeof v === 'string') { try { return JSON.parse(v) as string[]; } catch { return [v]; } }
      return [''];
    };
    return {
      name: String(mb.name ?? ''),
      qualification: String(mb.qualification ?? ''),
      role: String(mb.role ?? ''),
      image: String(mb.image ?? ''),
      desc: String(mb.desc ?? ''),
      ausbildung: toArr(mb.ausbildung),
      expertise: toArr(mb.expertise),
      berufserfahrung: toArr(mb.berufserfahrung),
    };
  });
}

// ── Bullet-list editor (add/remove/edit items) ────────────────────────────────
const BulletEditor: React.FC<{
  label: string;
  items: string[];
  onChange: (items: string[]) => void;
}> = ({ label, items, onChange }) => {
  const safe = items.length > 0 ? items : [''];
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {label}
        </Typography>
        <Button size="small" startIcon={<AddIcon />} onClick={() => onChange([...safe, ''])}>
          Punkt hinzufügen
        </Button>
      </Box>
      {safe.map((item, i) => (
        <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <TextField
            fullWidth size="small" multiline
            value={item}
            onChange={(e) => { const arr = [...safe]; arr[i] = e.target.value; onChange(arr); }}
            placeholder={`Punkt ${i + 1}`}
          />
          <IconButton size="small" color="error" disabled={safe.length === 1}
            onClick={() => onChange(safe.filter((_, idx) => idx !== i))}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
    </Box>
  );
};

// ── Team photo upload button ──────────────────────────────────────────────────
const PhotoUpload: React.FC<{ imageUrl: string; onUploaded: (url: string) => void }> = ({ imageUrl, onUploaded }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setErr('');
    try {
      const url = await uploadTeamImage(file);
      onUploaded(url);
    } catch {
      setErr('Upload fehlgeschlagen');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
      <Avatar
        src={imageUrl || undefined}
        sx={{ width: 96, height: 96, bgcolor: '#1737bc', fontSize: '2rem', border: '2px solid #e0e0e0' }}
      />
      <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFile} />
      <Button
        size="small"
        variant="outlined"
        startIcon={uploading ? <CircularProgress size={14} /> : <PhotoCameraIcon />}
        disabled={uploading}
        onClick={() => inputRef.current?.click()}
      >
        {uploading ? 'Hochladen…' : 'Foto hochladen'}
      </Button>
      {imageUrl && (
        <TextField
          label="Foto URL"
          fullWidth
          size="small"
          value={imageUrl}
          onChange={(e) => onUploaded(e.target.value)}
        />
      )}
      {err && <Typography color="error" variant="caption">{err}</Typography>}
    </Box>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const AdminUeberUns: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [content, setContent] = useState<AboutContent>(EMPTY_CONTENT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getSiteContent('about_us')
      .then((data) => {
        if (data && Object.keys(data).length > 0) {
          const loaded = data as unknown as AboutContent;
          setContent({
            ...EMPTY_CONTENT,
            ...loaded,
            stats: Array.isArray(loaded.stats) && loaded.stats.length ? loaded.stats : EMPTY_CONTENT.stats,
            values: Array.isArray(loaded.values) && loaded.values.length ? loaded.values : EMPTY_CONTENT.values,
            team: Array.isArray(loaded.team) ? normalizeTeam(loaded.team) : [],
          });
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true); setSuccess(false); setError('');
    try {
      await updateSiteContent('about_us', content as unknown as Record<string, unknown>);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 4000);
    } catch {
      setError('Fehler beim Speichern.');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field: keyof AboutContent, value: string) =>
    setContent((p) => ({ ...p, [field]: value }));

  const updateStat = (i: number, key: keyof Stat, val: string) =>
    setContent((p) => { const s = [...p.stats]; s[i] = { ...s[i], [key]: val }; return { ...p, stats: s }; });

  const updateValue = (i: number, key: keyof Value, val: string) =>
    setContent((p) => { const v = [...p.values]; v[i] = { ...v[i], [key]: val }; return { ...p, values: v }; });

  const updateMember = (i: number, key: keyof TeamMember, val: unknown) =>
    setContent((p) => { const t = [...p.team]; t[i] = { ...t[i], [key]: val }; return { ...p, team: t }; });

  const addMember = () =>
    setContent((p) => ({ ...p, team: [...p.team, { ...EMPTY_MEMBER }] }));

  const removeMember = (i: number) =>
    setContent((p) => ({ ...p, team: p.team.filter((_, idx) => idx !== i) }));

  if (loading) return <Box sx={{ p: 4 }}><Typography>Laden…</Typography></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>Über uns – Inhaltsverwaltung</Typography>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 3 }}>Inhalt erfolgreich gespeichert.</Alert>}
      {error   && <Alert severity="error"   sx={{ mb: 3 }}>{error}</Alert>}

      <Paper sx={{ p: 0, border: '1px solid #e8e8e8', boxShadow: 'none' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: '1px solid #e8e8e8', px: 2 }}>
          <Tab label="Hero & Intro" />
          <Tab label="Statistiken" />
          <Tab label="Werte" />
          <Tab label="Team" />
        </Tabs>

        {/* ── Tab 0: Hero & Intro ── */}
        <TabPanel value={tab} index={0}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} mb={2}>Hero-Bereich</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField label="Hero-Titel" fullWidth value={content.hero_title} onChange={(e) => updateField('hero_title', e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Hero-Untertitel" fullWidth value={content.hero_subtitle} onChange={(e) => updateField('hero_subtitle', e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Hero-Bild URL" fullWidth value={content.hero_image} onChange={(e) => updateField('hero_image', e.target.value)} />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" fontWeight={600} mb={2}>Intro-Bereich (Was wir machen)</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField label="Intro-Label" fullWidth value={content.intro_label} onChange={(e) => updateField('intro_label', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Intro-Titel" fullWidth value={content.intro_title} onChange={(e) => updateField('intro_title', e.target.value)} />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Intro-Text (Absätze mit Leerzeile trennen)"
                  fullWidth multiline rows={6}
                  value={content.intro_text}
                  onChange={(e) => updateField('intro_text', e.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Intro-Bild URL (Gruppenfoto)" fullWidth value={content.intro_image} onChange={(e) => updateField('intro_image', e.target.value)} />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" fontWeight={600} mb={2}>CTA-Banner</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField label="CTA-Titel" fullWidth value={content.cta_title} onChange={(e) => updateField('cta_title', e.target.value)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="CTA-Text" fullWidth value={content.cta_text} onChange={(e) => updateField('cta_text', e.target.value)} />
              </Grid>
            </Grid>
          </Box>
        </TabPanel>

        {/* ── Tab 1: Statistiken ── */}
        <TabPanel value={tab} index={1}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} mb={3}>Statistiken (4 Kennzahlen)</Typography>
            <Grid container spacing={3}>
              {content.stats.map((stat, i) => (
                <Grid item xs={12} sm={6} key={i}>
                  <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">Statistik {i + 1}</Typography>
                    <TextField label="Wert" fullWidth size="small" value={stat.value} onChange={(e) => updateStat(i, 'value', e.target.value)} />
                    <TextField label="Beschriftung" fullWidth size="small" value={stat.label} onChange={(e) => updateStat(i, 'label', e.target.value)} />
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        </TabPanel>

        {/* ── Tab 2: Werte ── */}
        <TabPanel value={tab} index={2}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} mb={3}>Unsere Werte (4 Karten)</Typography>
            <Grid container spacing={3}>
              {content.values.map((v, i) => (
                <Grid item xs={12} sm={6} key={i}>
                  <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">Wert {i + 1}</Typography>
                    <TextField label="Titel" fullWidth size="small" value={v.title} onChange={(e) => updateValue(i, 'title', e.target.value)} />
                    <TextField label="Beschreibung" fullWidth size="small" multiline rows={3} value={v.desc} onChange={(e) => updateValue(i, 'desc', e.target.value)} />
                    <TextField label="Bild URL" fullWidth size="small" value={v.image} onChange={(e) => updateValue(i, 'image', e.target.value)} />
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        </TabPanel>

        {/* ── Tab 3: Team ── */}
        <TabPanel value={tab} index={3}>
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6" fontWeight={600}>Team-Mitglieder</Typography>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={addMember} size="small">
                Mitglied hinzufügen
              </Button>
            </Box>

            {content.team.length === 0 && (
              <Typography color="text.secondary" sx={{ mb: 2 }}>Noch keine Team-Mitglieder vorhanden.</Typography>
            )}

            {content.team.map((member, i) => (
              <Paper key={i} variant="outlined" sx={{ p: 3, mb: 4 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight={700} color="#1737bc">
                    {member.name || `Mitglied ${i + 1}`}
                  </Typography>
                  <IconButton size="small" color="error" onClick={() => removeMember(i)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>

                <Grid container spacing={3}>
                  {/* Left: Photo upload */}
                  <Grid item xs={12} sm={3}>
                    <PhotoUpload
                      imageUrl={member.image}
                      onUploaded={(url) => updateMember(i, 'image', url)}
                    />
                  </Grid>

                  {/* Right: Basic info */}
                  <Grid item xs={12} sm={9}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Name" fullWidth size="small"
                          value={member.name}
                          onChange={(e) => updateMember(i, 'name', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Qualifikation (z.B. Diplom-Kaufmann)" fullWidth size="small"
                          value={member.qualification}
                          onChange={(e) => updateMember(i, 'qualification', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Rolle / Position" fullWidth size="small"
                          value={member.role}
                          onChange={(e) => updateMember(i, 'role', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Kurzbeschreibung (wird auf der Karte angezeigt)" fullWidth size="small" multiline rows={2}
                          value={member.desc}
                          onChange={(e) => updateMember(i, 'desc', e.target.value)}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Full width: Bullet sections */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <BulletEditor
                      label="Ausbildung"
                      items={member.ausbildung}
                      onChange={(arr) => updateMember(i, 'ausbildung', arr)}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <BulletEditor
                      label="Expertise & Spezialisierung"
                      items={member.expertise}
                      onChange={(arr) => updateMember(i, 'expertise', arr)}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <BulletEditor
                      label="Berufserfahrung & Erfolge"
                      items={member.berufserfahrung}
                      onChange={(arr) => updateMember(i, 'berufserfahrung', arr)}
                    />
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </Box>
        </TabPanel>

        {/* Save footer */}
        <Box sx={{ px: 3, py: 2, borderTop: '1px solid #e8e8e8', display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ minWidth: 140 }}>
            {saving ? 'Speichern…' : 'Speichern'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default AdminUeberUns;
